<script setup>
import { computed } from 'vue'
import { SUPPLIER_PATHS } from '@supplier/router/routeNames'
import { useSupplierSessionStore } from '@supplier/stores/useSupplierSessionStore'
import { useI18n } from 'vue-i18n'

import { useBreakpoint } from '@/composables/useBreakpoint'
import { useSupplierNav } from '@supplier/layouts/composables/useSupplierNav'

import SupplierImportBadge from '@supplier/layouts/components/SupplierImportBadge.vue'
import SupplierUserMenu from '@supplier/layouts/components/SupplierUserMenu.vue'
import AppButton from '@ui/app-button/AppButton.vue'
import AppIconButton from '@ui/app-icon-button/AppIconButton.vue'
import AppTooltip from '@ui/app-tooltip/AppTooltip.vue'

import EventBusV3 from '@/shared/eventBus'
import { SUPPLIER_CONNECT_STORE_MODAL_OPEN } from '@/shared/eventContract'

const { t } = useI18n()
const session = useSupplierSessionStore()
const { items: navItems } = useSupplierNav()
const { isUnderTablet, isMobile } = useBreakpoint()

const storeName = computed(() => session.store?.store_name || session.store?.store_url || '')
const storeAdminUrl = computed(() => (session.store?.store_url ? `https://${session.store.store_url}/admin` : ''))

function openConnectStore() {
  EventBusV3.emit(SUPPLIER_CONNECT_STORE_MODAL_OPEN)
}
</script>

<template>
  <header class="supplier-header">
    <div class="supplier-header-inner">
      <router-link
        :to="SUPPLIER_PATHS.DASHBOARD"
        class="supplier-header-logo"
        :aria-label="t('ui.supplier_portal.header.logo_label')"
        data-element="supplier-header-logo-link"
      />

      <nav
        v-if="!isUnderTablet"
        class="supplier-header-nav"
        :aria-label="t('ui.supplier_portal.header.nav_label')"
      >
        <app-button
          v-for="item in navItems"
          :key="item.key"
          :variant="item.isActive ? 'secondary' : 'menu'"
          size="sm"
          :to="item.to"
          :aria-current="item.isActive ? 'page' : undefined"
          :data-element="`supplier-header-nav-${item.key}-link`"
        >
          {{ item.label }}
        </app-button>
      </nav>

      <div class="supplier-header-actions">
        <supplier-import-badge />

        <app-tooltip
          v-if="session.hasStore"
          position="bottom"
          :text="storeName"
        >
          <template #trigger="{ bind }">
            <app-icon-button
              v-bind="bind"
              icon="Shopify"
              variant="no-fill"
              size="sm"
              :to="storeAdminUrl"
              :aria-label="t('ui.supplier_portal.header.open_store_admin', { store: storeName })"
              data-element="supplier-header-store-admin-link"
            />
          </template>
        </app-tooltip>

        <template v-else>
          <app-icon-button
            v-if="isMobile"
            icon="Shopify"
            variant="filled"
            size="sm"
            :aria-label="t('ui.supplier_portal.connect_store.title')"
            data-element="supplier-header-connect-store-btn"
            @click="openConnectStore"
          />
          <app-button
            v-else
            variant="primary"
            size="sm"
            left-icon="Shopify"
            data-element="supplier-header-connect-store-btn"
            @click="openConnectStore"
          >
            {{ t('ui.supplier_portal.connect_store.title') }}
          </app-button>
        </template>

        <supplier-user-menu :compact="isUnderTablet" />
      </div>
    </div>
  </header>
</template>

<style lang="scss" scoped>
@use '@/styles/breakpoints' as *;

.supplier-header {
  position: sticky;
  z-index: 100;
  top: 0;
  border-bottom: 1px solid var(--g-neutral-6);
  background-color: var(--g-neutral-8);

  &-inner {
    margin: 0 auto;
    display: flex;
    align-items: center;
    max-width: 1680px;
    height: var(--supplier-header-height);
    padding: 0 32px;
    gap: 24px;

    @include small-laptop-down {
      padding: 0 20px;
    }

    @include mobile-down {
      padding: 0 16px;
    }

    @include small-mobile-down {
      padding: 0 8px;
    }
  }

  &-logo {
    flex-shrink: 0;
    width: 111px;
    height: 20px;
    background-color: var(--g-neutral-1);
    mask-image: url('/images/zendrop-logo.svg');
    mask-position: left center;
    mask-repeat: no-repeat;
    mask-size: contain;
  }

  &-nav {
    display: flex;
    gap: 4px;
  }

  &-actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 12px;
  }
}
</style>
